import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, MapPin, Phone, Facebook, Instagram } from 'lucide-react';
import { theme } from '@/styles/theme';
import { Container } from '@/components/ui/Container';
import logoKph from '@/assets/images/logo-kph-blauw.png';

const FooterWrapper = styled.footer`
  background: ${theme.colors.primary.navy};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing[16]} 0 ${theme.spacing[8]};
  margin-top: auto;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing[8]};
  margin-bottom: ${theme.spacing[12]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[8]};
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FooterTitle = styled.h4`
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.primary.gold};
  margin-bottom: ${theme.spacing[2]};
`;

const FooterText = styled.p`
  font-size: ${theme.fontSizes.sm};
  line-height: ${theme.lineHeights.relaxed};
  color: ${theme.colors.secondary.cream};
`;

const FooterLink = styled(Link)`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.secondary.cream};
  text-decoration: none;
  transition: color ${theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    color: ${theme.colors.primary.gold};
  }
`;

const FooterLinkExternal = styled.a`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.secondary.cream};
  text-decoration: none;
  transition: color ${theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    color: ${theme.colors.primary.gold};
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.secondary.cream};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[2]};
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(212, 175, 55, 0.1);
  color: ${theme.colors.primary.gold};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary.gold};
    color: ${theme.colors.primary.navy};
    transform: translateY(-2px);
  }
`;

const FooterBottom = styled.div`
  padding-top: ${theme.spacing[8]};
  border-top: 1px solid rgba(212, 175, 55, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.secondary.cream};
`;

const LegalLinks = styled.div`
  display: flex;
  gap: ${theme.spacing[6]};
  flex-wrap: wrap;

  @media (max-width: ${theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
`;

const LogoImage = styled.img`
  height: 48px;
  width: auto;
  object-fit: contain;
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const LogoTitle = styled.span`
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.neutral.white};
  line-height: 1.2;
`;

const LogoSubtitle = styled.span`
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.primary.lightGold};
  font-weight: ${theme.fontWeights.medium};
`;

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterWrapper>
      <Container>
        <FooterContent>
          <FooterSection>
            <Logo>
              <LogoImage src={logoKph} alt="KPH Sint-Cecilia Logo" />
              <LogoText>
                <LogoTitle>Koninklijke Poperingse Harmonie</LogoTitle>
                <LogoSubtitle>Sint-Cecilia</LogoSubtitle>
              </LogoText>
            </Logo>
            <FooterText>
              Meer dan een eeuw passie voor harmoniemuziek in Poperinge en omstreken.
            </FooterText>
            <SocialLinks>
              <SocialIcon href="https://www.facebook.com/kphsintcecilia" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <Facebook size={20} />
              </SocialIcon>
              <SocialIcon href="https://www.instagram.com/kphsintcecilia/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram size={20} />
              </SocialIcon>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Snelle Links</FooterTitle>
            <FooterLink to="/over-ons">Over Ons</FooterLink>
            <FooterLink to="/groepen">Onze Groepen</FooterLink>
            <FooterLink to="/evenementen">Evenementen</FooterLink>
            <FooterLink to="/berichten">Berichten</FooterLink>
            <FooterLink to="/sponsors">Sponsors</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Contact</FooterTitle>
            <ContactItem>
              <MapPin size={18} />
              <span>
                Kasteeltuinstraat 9a<br />
                8970 Poperinge<br />
                België
              </span>
            </ContactItem>
            <ContactItem>
              <Mail size={18} />
              <FooterLinkExternal href="mailto:info@kph.be">
                info@kph.be
              </FooterLinkExternal>
            </ContactItem>
            <ContactItem>
              <Phone size={18} />
              <FooterLinkExternal href="tel:+32123456789">
                +32 486 21 87 92
              </FooterLinkExternal>
            </ContactItem>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Nieuwsbrief</FooterTitle>
            <FooterText>
              Blijf op de hoogte van onze concerten en evenementen.
            </FooterText>
            <FooterLink to="/contact#newsletter">
              Schrijf je in →
            </FooterLink>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright>
            © {currentYear} Koninklijke Poperingse Harmonie Sint-Cecilia. Alle rechten voorbehouden.
          </Copyright>
          <LegalLinks>
            <FooterLink to="/privacy">Privacybeleid</FooterLink>
            <FooterLink to="/cookies">Cookiebeleid</FooterLink>
            <FooterLink to="/terms">Algemene Voorwaarden</FooterLink>
          </LegalLinks>
        </FooterBottom>
      </Container>
    </FooterWrapper>
  );
};
